import { hasWindow } from "@omni/utils/dom.utils";
import { retryWithDelay } from "@omni/utils/function.utils";
import React, { Context, use, useState } from "react";

import { getAssetLocation } from "@/utils/asset.utils";
import { useScript } from "@/utils/hooks/useScript.hook";

export type DeviceFingerPrintingContextValues = {
    loadDeviceFingerPrinting: () => void;
    getDeviceFingerPrint: () => Promise<string | undefined>;
};

const defaultContext: DeviceFingerPrintingContextValues = {
    loadDeviceFingerPrinting: () => {},
    getDeviceFingerPrint: async () => {
        return undefined;
    }
};

const DeviceFingerPrintingContext: Context<DeviceFingerPrintingContextValues> =
    React.createContext<DeviceFingerPrintingContextValues>(defaultContext);

export type DeviceFingerPrintingProviderProps = {
    children?: React.ReactNode;
};

const DeviceFingerPrintingProvider = ({
    children
}: DeviceFingerPrintingProviderProps) => {
    const [canLoad, setCanLoad] = useState(false);
    const { isScriptLoaded } = useScript({
        src: `${getAssetLocation()}/scripts/iovation-loader-5.x.js`,
        id: "iovation",
        disabled: !canLoad,
        configFn: () => {
            window.IGLOO = window.IGLOO || {
                enable_rip: true, // Enable Real IP protection.
                enable_flash: false, // Disable flash
                install_flash: false, // Don't ask user to install flash
                loader: {
                    version: "general5", // Non-experimental 5.x updates
                    fp_static: false // Don't load 1st party resources
                }
            };
        }
    });

    const loadDeviceFingerPrinting = () => {
        setCanLoad(true);
    };

    const getBlackBox = async () => {
        return await retryWithDelay({
            fn: async () => {
                const bbData = window.IGLOO?.getBlackbox?.();
                if (bbData?.finished) {
                    return Promise.resolve(encodeURIComponent(bbData.blackbox));
                } else {
                    throw new Error("Blackbox not finished");
                }
            }
        });
    };

    const getDeviceFingerPrint = async () => {
        if (!hasWindow()) return Promise.resolve(undefined);
        try {
            await isScriptLoaded();
            return await getBlackBox();
        } catch {
            return Promise.resolve(undefined);
        }
    };

    return (
        <DeviceFingerPrintingContext
            value={{
                loadDeviceFingerPrinting,
                getDeviceFingerPrint
            }}
        >
            {children}
        </DeviceFingerPrintingContext>
    );
};

const useDeviceFingerPrinting = (): DeviceFingerPrintingContextValues => {
    const context = use<DeviceFingerPrintingContextValues>(
        DeviceFingerPrintingContext
    );
    if (context === undefined) {
        throw new Error("DeviceFingerPrintingContext is undefined.");
    }
    return context;
};

export { DeviceFingerPrintingProvider, useDeviceFingerPrinting };
